// Arlington County, VA sprayground / interactive water-feature source catalog.
// Sister of shared/splash-pads.ts (which is DC DPR). Coordinates are Census-
// geocoded via the US Census Bureau Public_AR_Current benchmark (2026-06-20).
//
// This is the DATA only. The Arlington seed turns each ACTIVE entry into a
// crowd-reportable Pool (kind:"splash", poolId:"splash-<id>"), reported on the
// `main` section, surfaced on the Map + Splash tab. Lives in shared/ so the seed
// can import it without pulling in frontend code.
//
// ── SCHEDULE PROVENANCE ──────────────────────────────────────────────────────
// Per-weekday hours below are from Arlington County's published 2026 weekly
// sprayground schedule (provided 2026-06-20). They are still only a SCHEDULE HINT
// — the app's source of truth is the live crowd report, which degrades gracefully
// if the County changes hours mid-season.
//
// Unlike DC DPR spray parks (uniform 10am–6pm season), Arlington spraygrounds run
// DIFFERENT hours each weekday, so each entry carries a full 7-day schedule
// (index 0=Sun … 6=Sat; null = closed that day) that maps directly onto the
// existing SectionSchedule.days model in shared/types.ts.

import type { DayHours } from "./types";

export interface Sprayground {
  id: string;
  name: string;
  address: string;
  /** Arlington neighborhood (there are no wards here — replaces DC's `ward`). */
  neighborhood: string;
  lat: number;
  lng: number;
  /** Weekly hours; index 0=Sun … 6=Sat. null = closed that day. */
  days: Array<DayHours | null>;
  /** Human-readable hours summary shown on the listing card. */
  scheduleNote: string;
  /** Source confidence for the entry; informational only. */
  confidence: "high" | "medium" | "low";
  /**
   * Closed for the whole 2026 season (e.g. long-term maintenance). Mapped to the
   * Pool `closedForSeason` flag: stays on the map as a non-reportable
   * "Closed for the season" card. When set, `days` is ignored.
   */
  closedForSeason?: boolean;
}

/** Season window — Memorial Day weekend through Labor Day weekend, 2026. */
export const ARLINGTON_SPLASH_SEASON = {
  typical: "Memorial Day weekend through Labor Day weekend",
  hours: "Hours vary by location and day of week — see each listing",
  schedule2026: "Approx. May 23 – September 7, 2026 (confirm with Arlington Parks & Recreation)",
  start: "2026-05-23",
  end: "2026-09-07",
  notes: "",
} as const;

// Convenience builders for the repeated weekday windows.
const h = (open: string, close: string): DayHours => ({ open, close });
//                     Sun        Mon        Tue        Wed        Thu        Fri        Sat

export const ARLINGTON_SPRAYGROUNDS: Sprayground[] = [
  {
    id: "drew-park",
    name: "Drew Park Sprayground",
    address: "2310 S Kenmore St, Arlington, VA 22206",
    neighborhood: "Nauck / Green Valley",
    lat: 38.84903,
    lng: -77.08521,
    // Mon & Thu 2–8pm; Wed 10am–3pm; Fri 10am–8pm; Sat & Sun noon–8pm; closed Tue.
    days: [h("12:00", "20:00"), h("14:00", "20:00"), null, h("10:00", "15:00"), h("14:00", "20:00"), h("10:00", "20:00"), h("12:00", "20:00")],
    scheduleNote: "Mon & Thu 2–8pm · Wed 10am–3pm · Fri 10am–8pm · Sat & Sun noon–8pm · closed Tue",
    confidence: "high",
  },
  {
    id: "hayes-park",
    name: "Hayes Park Sprayground",
    address: "1516 N Lincoln St, Arlington, VA 22201",
    neighborhood: "Clarendon / Lyon Park",
    lat: 38.88952,
    lng: -77.10338,
    // Mon & Thu 10am–3pm; Tue & Fri 2–8pm; Sat & Sun noon–8pm; closed Wed.
    days: [h("12:00", "20:00"), h("10:00", "15:00"), h("14:00", "20:00"), null, h("10:00", "15:00"), h("14:00", "20:00"), h("12:00", "20:00")],
    scheduleNote: "Mon & Thu 10am–3pm · Tue & Fri 2–8pm · Sat & Sun noon–8pm · closed Wed",
    confidence: "high",
  },
  {
    id: "lyon-village-park",
    name: "Lyon Village Park Sprayground",
    address: "1800 N Highland St, Arlington, VA 22201",
    neighborhood: "Lyon Village",
    lat: 38.89259,
    lng: -77.0946,
    // Mon & Tue 10am–3pm; Wed & Fri 2–8pm; Sat & Sun noon–8pm; closed Thu.
    days: [h("12:00", "20:00"), h("10:00", "15:00"), h("10:00", "15:00"), h("14:00", "20:00"), null, h("14:00", "20:00"), h("12:00", "20:00")],
    scheduleNote: "Mon & Tue 10am–3pm · Wed & Fri 2–8pm · Sat & Sun noon–8pm · closed Thu",
    confidence: "high",
  },
  {
    id: "virginia-highlands-park",
    name: "Virginia Highlands Park Sprayground",
    address: "1600 S Hayes St, Arlington, VA 22202",
    neighborhood: "Pentagon City / Aurora Highlands",
    lat: 38.85889,
    lng: -77.05944,
    // Daily 10am–8pm.
    days: [h("10:00", "20:00"), h("10:00", "20:00"), h("10:00", "20:00"), h("10:00", "20:00"), h("10:00", "20:00"), h("10:00", "20:00"), h("10:00", "20:00")],
    scheduleNote: "Daily 10am–8pm",
    confidence: "high",
  },
  {
    id: "penrose-square",
    name: "Penrose Square Water Feature",
    address: "2597 Columbia Pike, Arlington, VA 22204",
    neighborhood: "Penrose / Columbia Pike",
    lat: 38.86312,
    lng: -77.08502,
    // Plaza fountain: 7am–10pm six days; closed Wed.
    days: [h("07:00", "22:00"), h("07:00", "22:00"), h("07:00", "22:00"), null, h("07:00", "22:00"), h("07:00", "22:00"), h("07:00", "22:00")],
    scheduleNote: "Plaza water feature · 7am–10pm · closed Wed",
    confidence: "high",
  },
  {
    id: "mosaic-park",
    name: "Mosaic Park Water Feature",
    address: "541 N Pollard St, Arlington, VA 22203",
    neighborhood: "Ballston",
    lat: 38.87831,
    lng: -77.10659,
    // Park water feature: 7am–10pm daily.
    days: [h("07:00", "22:00"), h("07:00", "22:00"), h("07:00", "22:00"), h("07:00", "22:00"), h("07:00", "22:00"), h("07:00", "22:00"), h("07:00", "22:00")],
    scheduleNote: "Park water feature · 7am–10pm daily",
    confidence: "high",
  },
  {
    id: "long-bridge-park",
    name: "Long Bridge Park Water Feature",
    address: "475 Long Bridge Dr, Arlington, VA 22202",
    neighborhood: "Crystal City / National Landing",
    lat: 38.8625,
    lng: -77.0485,
    // Esplanade interactive water feature: 7am–10pm daily.
    days: [h("07:00", "22:00"), h("07:00", "22:00"), h("07:00", "22:00"), h("07:00", "22:00"), h("07:00", "22:00"), h("07:00", "22:00"), h("07:00", "22:00")],
    scheduleNote: "Esplanade water feature · 7am–10pm daily",
    confidence: "high",
  },
];
